import { StateCreator } from 'zustand';
import type { EntryCommandWorkflowState } from '@/services/commands/EntryCommandRegistry';
import { EMPTY_ENTRY_COMMAND_STATE } from '@/services/commands/EntryCommandRegistry';

export type AgentMode = 'assistant' | 'autonomous' | 'creative' | 'research';

/**
 * ConversationMode — user-selected routing and hierarchical execution mode.
 *
 * - 'orchestrated' : Conductor chooses a path; each concrete agent execution
 *                    receives one of the established T1 communication modes
 * - 'direct'     : User ↔ one agent (no delegation)
 * - 'department' : User ↔ head; head ↔ own workers; cross-dept blocked
 * - 'boardroom'  : User ↔ seated heads; heads ↔ each other; workers cannot be seated
 *
 * Default remains 'direct'.
 */
export const CONVERSATION_MODES = ['direct', 'department', 'boardroom', 'orchestrated'] as const;
export type ConversationMode = typeof CONVERSATION_MODES[number];

export function isConversationMode(value: unknown): value is ConversationMode {
    return typeof value === 'string' && (CONVERSATION_MODES as readonly string[]).includes(value);
}

export interface ApprovalRequest {
    id: string;
    content: string;
    type: string;
    timestamp: number;
    expiresAt: number;
    resolve: (approved: boolean) => void;
}

export interface AgentUISlice {
    isAgentOpen: boolean;
    toggleAgentWindow: () => void;

    isCommandBarDetached: boolean;
    isCommandBarCollapsed: boolean;
    commandBarPosition: 'left' | 'center' | 'right';
    commandBarInput: string;
    commandBarAttachments: File[];
    setCommandBarDetached: (detached: boolean) => void;
    setCommandBarCollapsed: (collapsed: boolean) => void;
    setCommandBarPosition: (position: 'left' | 'center' | 'right') => void;
    setCommandBarInput: (input: string) => void;
    setCommandBarAttachments: (attachments: File[]) => void;
    resetCommandBar: () => void;

    agentMode: AgentMode;
    setAgentMode: (mode: AgentMode) => void;
    isAgentProcessing: boolean;
    setAgentProcessing: (isProcessing: boolean) => void;

    agentAbortController: AbortController | null;
    startAgentExecution: () => AbortSignal;
    stopAgent: () => void;

    pendingApproval: ApprovalRequest | null;
    requestApproval: (content: string, type: string) => Promise<boolean>;
    resolveApproval: (approved: boolean, approvalId?: string) => void;

    rightPanelView: 'messages' | 'archives';
    setRightPanelView: (view: 'messages' | 'archives') => void;

    chatChannel: 'indii' | 'agent';
    setChatChannel: (channel: 'indii' | 'agent') => void;
    activeAgentProvider: 'direct' | 'native';
    setActiveAgentProvider: (provider: 'direct' | 'native') => void;

    conversationMode: ConversationMode;
    activeDepartmentId: string | null;
    directTargetAgentId: string | null;
    setConversationMode: (mode: ConversationMode) => void;
    setActiveDepartmentId: (deptId: string | null) => void;
    setDirectTargetAgentId: (agentId: string | null) => void;

    isKnowledgeBaseEnabled: boolean;
    setKnowledgeBaseEnabled: (enabled: boolean) => void;

    agentWindowSize: { width: number; height: number };
    setAgentWindowSize: (size: { width: number; height: number }) => void;

    entryCommandWorkflow: EntryCommandWorkflowState;
    setEntryCommandWorkflow: (workflow: EntryCommandWorkflowState) => void;
    updateEntryCommandWorkflow: (updates: Partial<EntryCommandWorkflowState>) => void;
    clearEntryCommandWorkflow: () => void;
}

export function buildAgentUIState(
    set: Parameters<StateCreator<AgentUISlice>>[0],
    get: Parameters<StateCreator<AgentUISlice>>[1]
): AgentUISlice {
    return {
        isAgentOpen: false,
        isCommandBarDetached: false,
        isCommandBarCollapsed: false,
        commandBarPosition: (typeof window !== 'undefined' && (['left', 'center', 'right'].includes(localStorage.getItem('indii_commandBarPosition') || '') ? localStorage.getItem('indii_commandBarPosition') as 'left' | 'center' | 'right' : 'center')) || 'center',
        commandBarInput: '',
        commandBarAttachments: [],
        agentMode: 'assistant',
        isAgentProcessing: false,
        agentAbortController: null,
        pendingApproval: null,
        rightPanelView: 'messages',
        chatChannel: 'indii',
        activeAgentProvider: 'native',
        conversationMode: 'direct',
        activeDepartmentId: null,
        directTargetAgentId: null,
        isKnowledgeBaseEnabled: true,
        agentWindowSize: { width: 500, height: 800 },
        entryCommandWorkflow: { ...EMPTY_ENTRY_COMMAND_STATE },

        toggleAgentWindow: () => set((state) => ({ isAgentOpen: !state.isAgentOpen })),
        setCommandBarDetached: (detached) => set({ isCommandBarDetached: detached }),
        setCommandBarCollapsed: (collapsed) => set({ isCommandBarCollapsed: collapsed }),
        setCommandBarPosition: (position) => {
            if (typeof window !== 'undefined') {
                localStorage.setItem('indii_commandBarPosition', position);
            }
            set({ commandBarPosition: position });
        },
        setCommandBarInput: (input) => set({ commandBarInput: input }),
        setCommandBarAttachments: (attachments) => set({ commandBarAttachments: attachments }),
        resetCommandBar: () => set({ isCommandBarDetached: false, isCommandBarCollapsed: false, commandBarPosition: 'center' }),
        setAgentMode: (mode) => set({ agentMode: mode }),
        setAgentProcessing: (isProcessing) => set({ isAgentProcessing: isProcessing }),

        startAgentExecution: () => {
            const existing = get().agentAbortController;
            if (existing) existing.abort('New execution started');
            const controller = new AbortController();
            set({ agentAbortController: controller, isAgentProcessing: true });
            return controller.signal;
        },

        stopAgent: () => {
            const { agentAbortController } = get();
            if (agentAbortController) agentAbortController.abort('User requested stop');
            set({ agentAbortController: null, isAgentProcessing: false });
        },

        setRightPanelView: (view) => set({ rightPanelView: view }),
        setChatChannel: (channel) => set({ chatChannel: channel }),
        setActiveAgentProvider: (provider) => set({ activeAgentProvider: provider }),
        setConversationMode: (mode) => set({ conversationMode: mode }),
        setActiveDepartmentId: (deptId) => set({ activeDepartmentId: deptId }),
        setDirectTargetAgentId: (agentId) => set({ directTargetAgentId: agentId }),
        setKnowledgeBaseEnabled: (enabled) => set({ isKnowledgeBaseEnabled: enabled }),
        setAgentWindowSize: (size) => set({ agentWindowSize: size }),
        setEntryCommandWorkflow: (workflow) => set({ entryCommandWorkflow: workflow }),
        updateEntryCommandWorkflow: (updates) => set((state) => ({
            entryCommandWorkflow: {
                ...state.entryCommandWorkflow,
                ...updates,
                updatedAt: Date.now(),
            },
        })),
        clearEntryCommandWorkflow: () => set({ entryCommandWorkflow: { ...EMPTY_ENTRY_COMMAND_STATE } }),

        requestApproval: (content: string, type: string): Promise<boolean> => {
            return new Promise((resolve) => {
                get().pendingApproval?.resolve(false);
                const timestamp = Date.now();
                const request: ApprovalRequest = {
                    id: crypto.randomUUID(),
                    content,
                    type,
                    timestamp,
                    expiresAt: timestamp + 120_000,
                    resolve,
                };
                set({ pendingApproval: request });
                setTimeout(() => {
                    if (get().pendingApproval?.id === request.id) {
                        get().resolveApproval(false, request.id);
                    }
                }, 120_000);
            });
        },

        resolveApproval: (approved: boolean, approvalId?: string) => {
            const { pendingApproval } = get();
            if (pendingApproval && (!approvalId || pendingApproval.id === approvalId)) {
                pendingApproval.resolve(approved && Date.now() < pendingApproval.expiresAt);
                set({ pendingApproval: null });
            }
        },
    };
}
